import { axios } from '@pukka/auth';
import { MOKA_GOAUTH_SERVICE } from '@pukka/common/constants';
import { UseQueryOptions, useQuery } from '@tanstack/react-query';
import fileDownload from 'js-file-download';

import { SyncBillModel } from '../models';

const SYNC_BILL_SERVICE_URL = `${MOKA_GOAUTH_SERVICE}/sync-bill/v1`;

type ExportParams = {
  startDate: string; // e.g. 2023-01-01T00:00:00+07:00
  endDate: string; // e.g. 2023-12-31T23:59:59+07:00
  outletName: string; // e.g. Outlet 2
};

type ExportBillParams = ExportParams & { status: string }; // CANCELLED

type ExportVoidParams = ExportParams & { statuses: string }; // COMPLETE,CANCELLED

type ApiParams = {
  dineInOnly?: boolean;
  size?: number;
  billSchemas?: number;
  page?: number; // start from 1
  startDate: string; // e.g. 2023-01-01T00:00:00+07:00
  endDate: string; // e.g. 2023-12-31T23:59:59+07:00
  statuses: string; // e.g. COMPLETE / CANCELLED / COMPLETE,CANCELLED
  sort?: string; // e.g. updatedAt,desc
  orderId?: string;
  itemName?: string;
};

async function getBills(outletId: number | number[], params: Omit<ApiParams, 'sort'>): Promise<SyncBillModel.BillsResponse> {
  if (Array.isArray(outletId)) {
    const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/all`, {
      params: { size: 50, billSchemas: -1, page: 1, deep: true, sort: 'updatedAt,desc', outletIds: outletId.join(','), ...params },
    });

    return data;
  }

  const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}`, {
    params: { size: 50, billSchemas: -1, page: 1, deep: true, sort: 'updatedAt,desc', ...params },
  });

  return data;
}

async function getBillSummary(outletId: number | number[], params: Omit<ApiParams, 'sort'>) {
  if (Array.isArray(outletId)) {
    const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/all/summary`, {
      params: { billSchemas: -1, outletIds: outletId.join(','), ...params },
    });

    return data;
  }

  const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/summary`, {
    params: { billSchemas: -1, ...params },
  });

  return data;
}

async function getBillDetail(outletId: number, billId: number): Promise<SyncBillModel.Bill> {
  const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/bill/${billId}`, {});

  return data;
}

async function getBillExportFile(outletId: number, params: ExportBillParams, downloader = fileDownload) {
  const { data, headers } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/export`, {
    params,
    headers: { Accept: '*/*' },
    withCredentials: false,
  });

  if (data && headers.filename) {
    downloader(data, headers.filename);
  }

  return data;
}

async function getVoidItems(outletId: number | number[], params: Omit<ApiParams, 'sort'>): Promise<SyncBillModel.VoidItemsResponse> {
  if (Array.isArray(outletId)) {
    const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/all/void-items`, {
      params: { size: 50, page: 1, deep: true, sort: 'cancelled_time,desc', outletIds: outletId.join(','), ...params },
    });

    return data;
  }

  const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/void-items`, {
    params: { size: 50, page: 1, deep: true, sort: 'cancelled_time,desc', ...params },
  });

  return data;
}

async function getVoidItemSummary(outletId: number | number[], params: Omit<ApiParams, 'sort'>): Promise<SyncBillModel.VoidItemSummary> {
  if (Array.isArray(outletId)) {
    const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/all/summary/void-item`, {
      params: { outletIds: outletId.join(','), ...params },
    });

    return data;
  }

  const { data } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/summary/void-item`, {
    params: { ...params },
  });

  return data;
}

async function getVoidItemExportFile(outletId: number, params: ExportVoidParams, downloader = fileDownload) {
  const { data, headers } = await axios.get(`${SYNC_BILL_SERVICE_URL}/bills/outlet/${outletId}/export/void-items`, {
    params,
    headers: { Accept: '*/*' },
    withCredentials: false,
  });

  if (data && headers.filename) {
    downloader(data, headers.filename);
  }

  return data;
}

function useBillSummary(
  outletId: number | number[],
  params: Omit<ApiParams, 'sort' | 'page' | 'size'>,
  options?: UseQueryOptions<Awaited<ReturnType<typeof getBillSummary>>>,
) {
  return useQuery<SyncBillModel.BillSummary>({
    queryKey: ['bill-summary', outletId, params.startDate, params.endDate, params.orderId],
    queryFn: () => getBillSummary(outletId, params),
    ...options,
  });
}

function useVoidItemSummary(
  outletId: number | number[],
  params: Omit<ApiParams, 'sort' | 'page' | 'size'>,
  options?: UseQueryOptions<Awaited<ReturnType<typeof getBillSummary>>>,
) {
  return useQuery<SyncBillModel.VoidItemSummary>({
    queryKey: ['void-item-summary', outletId, params.startDate, params.endDate, params.itemName],
    queryFn: () => getVoidItemSummary(outletId, params),
    ...options,
  });
}

export {
  SYNC_BILL_SERVICE_URL,
  getBills,
  getBillSummary,
  getVoidItems,
  getVoidItemSummary,
  useBillSummary,
  useVoidItemSummary,
  getBillDetail,
  getBillExportFile,
  getVoidItemExportFile,
};
